// CANONICAL COPY (GH #7115). src/constants/frameworks/dora.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

// DORA — Regulation (EU) 2022/2554 — Digital Operational Resilience Act.
// Level 1 articles only; Level 2 RTS/ITS out of scope.
// Scope is deliberately entity-facing: Chapters II–VII (Art. 5–30) and Art. 45
// (financial entities' ICT third-party obligations). The Art. 31–44 oversight
// framework for Critical ICT Third-Party Providers (CTPPs) — Lead Overseer
// designation, oversight fees, joint examination teams — governs the ESAs'
// supervision of CTPPs directly, not the compliance posture of a financial
// entity tenant, so it is intentionally omitted rather than an oversight gap.
// Source: Regulation (EU) 2022/2554 (OJ L 333, 27.12.2022, p. 1).
// Canonical control list mirrored in docs/dora/dora-regulation-2022-2554-controls.md.
//
// externalId scheme: `DORA-ART<n>-<sub>` where `<sub>` is a local counter per
// article covering a distinct operational obligation.
//
// Multiple mappings with the same externalId are intentional — the last entry
// per externalId is used for the scfLookup (cross-reference display), while
// all entries contribute to controlToFrameworks and externalRefs. The
// "primary" TCF (marked with a `// primary` comment) is the last mapping for
// each externalId.

const FRAMEWORK_DORA = {
  id: 'fw-dora',
  type: 'law',
  name: 'Digital Operational Resilience Act',
  shortName: 'DORA',
  abbrev: 'DORA',
  description:
    'EU Regulation (EU) 2022/2554 — directly applicable digital operational resilience requirements for financial entities and their ICT third-party service providers. Applies from 17 January 2025.',
  color: '#0ea5e9',
  version: '2022/2554',
  jurisdiction: 'EU',
  sectionKeyDepth: 1,
  sections: [
    { key: 'ART5', name: 'Governance and organisation' },
    { key: 'ART6', name: 'ICT risk-management framework' },
    { key: 'ART7', name: 'ICT systems, protocols and tools' },
    { key: 'ART8', name: 'Identification' },
    { key: 'ART9', name: 'Protection and prevention' },
    { key: 'ART10', name: 'Detection' },
    { key: 'ART11', name: 'Response and recovery' },
    { key: 'ART12', name: 'Backup policies and procedures' },
    { key: 'ART13', name: 'Learning and evolving' },
    { key: 'ART14', name: 'Communication' },
    { key: 'ART16', name: 'Simplified ICT risk-management framework' },
    { key: 'ART17', name: 'ICT-related incident management process' },
    { key: 'ART18', name: 'Classification of incidents and cyber threats' },
    { key: 'ART19', name: 'Reporting of major incidents and cyber threats' },
    { key: 'ART24', name: 'General requirements for testing' },
    { key: 'ART25', name: 'Testing of ICT tools and systems' },
    { key: 'ART26', name: 'Advanced testing (TLPT)' },
    { key: 'ART27', name: 'Requirements for testers' },
    { key: 'ART28', name: 'General principles' },
    { key: 'ART29', name: 'Preliminary assessment' },
    { key: 'ART30', name: 'Key contractual provisions' },
    { key: 'ART45', name: 'Information and intelligence sharing' },
  ],
  mappings: [
    // ── Chapter II — ICT risk management ──────────────────────────────────

    // Article 5 — Governance and organisation
    { externalId: 'DORA-ART5-1', section: 'ART5', scfId: 'SCF-GOV-001', ref: 'Art. 5' }, // management-approved IS policy anchors the ICT RMF
    { externalId: 'DORA-ART5-1', section: 'ART5', scfId: 'SCF-GOV-004', ref: 'Art. 5' }, // management responsibilities
    {
      primary: true,
      externalId: 'DORA-ART5-1',
      section: 'ART5',
      scfId: 'SCF-GOV-012',
      ref: 'Art. 5',
    }, // primary — management body accountability
    { externalId: 'DORA-ART5-2', section: 'ART5', scfId: 'SCF-GOV-012', ref: 'Art. 5(4)' }, // management body accountability (knowledge/skills)
    {
      primary: true,
      externalId: 'DORA-ART5-2',
      section: 'ART5',
      scfId: 'SCF-PPL-003',
      ref: 'Art. 5(4)',
    }, // primary — training of management body

    // Article 6 — ICT risk-management framework
    { externalId: 'DORA-ART6-1', section: 'ART6', scfId: 'SCF-GOV-011', ref: 'Art. 6' }, // documented operating procedures
    { externalId: 'DORA-ART6-1', section: 'ART6', scfId: 'SCF-RSK-001', ref: 'Art. 6' }, // risk assessment process
    {
      primary: true,
      externalId: 'DORA-ART6-1',
      section: 'ART6',
      scfId: 'SCF-GOV-001',
      ref: 'Art. 6',
    }, // primary — IS policy (framework documented, annual review)
    { externalId: 'DORA-ART6-2', section: 'ART6', scfId: 'SCF-RSK-002', ref: 'Art. 6(2)' }, // IS objectives
    {
      primary: true,
      externalId: 'DORA-ART6-2',
      section: 'ART6',
      scfId: 'SCF-RSK-001',
      ref: 'Art. 6(2)',
    }, // primary — risk assessment integrated with enterprise risk
    { externalId: 'DORA-ART6-3', section: 'ART6', scfId: 'SCF-AUD-002', ref: 'Art. 6(6)' }, // compliance with policies and standards
    {
      primary: true,
      externalId: 'DORA-ART6-3',
      section: 'ART6',
      scfId: 'SCF-AUD-001',
      ref: 'Art. 6(6)',
    }, // primary — independent review of security

    // Article 7 — ICT systems, protocols and tools
    { externalId: 'DORA-ART7-1', section: 'ART7', scfId: 'SCF-OPS-005', ref: 'Art. 7' }, // configuration management
    { externalId: 'DORA-ART7-1', section: 'ART7', scfId: 'SCF-BCM-002', ref: 'Art. 7' }, // ICT continuity planning
    {
      primary: true,
      externalId: 'DORA-ART7-1',
      section: 'ART7',
      scfId: 'SCF-OPS-002',
      ref: 'Art. 7',
    }, // primary — capacity management (sufficient capacity, resilience)

    // Article 8 — Identification
    { externalId: 'DORA-ART8-1', section: 'ART8', scfId: 'SCF-AST-001', ref: 'Art. 8(1)' }, // asset inventory
    {
      primary: true,
      externalId: 'DORA-ART8-1',
      section: 'ART8',
      scfId: 'SCF-AST-004',
      ref: 'Art. 8(1)',
    }, // primary — information classification (business functions and assets)
    { externalId: 'DORA-ART8-2', section: 'ART8', scfId: 'SCF-VDR-003', ref: 'Art. 8(4)' }, // ICT supply chain security
    {
      primary: true,
      externalId: 'DORA-ART8-2',
      section: 'ART8',
      scfId: 'SCF-AST-001',
      ref: 'Art. 8(4)',
    }, // primary — asset inventory with dependencies
    { externalId: 'DORA-ART8-3', section: 'ART8', scfId: 'SCF-GOV-007', ref: 'Art. 8(5)' }, // threat intelligence
    {
      primary: true,
      externalId: 'DORA-ART8-3',
      section: 'ART8',
      scfId: 'SCF-RSK-001',
      ref: 'Art. 8(5)',
    }, // primary — continuous risk identification

    // Article 9 — Protection and prevention
    { externalId: 'DORA-ART9-1', section: 'ART9', scfId: 'SCF-OPS-001', ref: 'Art. 9(1)' }, // endpoint security
    { externalId: 'DORA-ART9-1', section: 'ART9', scfId: 'SCF-NET-001', ref: 'Art. 9(1)' }, // network security management
    {
      primary: true,
      externalId: 'DORA-ART9-1',
      section: 'ART9',
      scfId: 'SCF-OPS-007',
      ref: 'Art. 9(1)',
    }, // primary — security monitoring of ICT systems
    { externalId: 'DORA-ART9-2', section: 'ART9', scfId: 'SCF-IAM-004', ref: 'Art. 9(3)(c)' }, // access rights lifecycle
    { externalId: 'DORA-ART9-2', section: 'ART9', scfId: 'SCF-IAM-005', ref: 'Art. 9(3)(c)' }, // privileged access
    { externalId: 'DORA-ART9-2', section: 'ART9', scfId: 'SCF-IAM-006', ref: 'Art. 9(3)(c)' }, // information access restriction (least privilege)
    {
      primary: true,
      externalId: 'DORA-ART9-2',
      section: 'ART9',
      scfId: 'SCF-IAM-001',
      ref: 'Art. 9(3)(c)',
    }, // primary — access control policy
    { externalId: 'DORA-ART9-3', section: 'ART9', scfId: 'SCF-DAT-001', ref: 'Art. 9(2)' }, // records and data protection
    { externalId: 'DORA-ART9-3', section: 'ART9', scfId: 'SCF-IAM-008', ref: 'Art. 9(2)' }, // secure authentication (strong auth)
    {
      primary: true,
      externalId: 'DORA-ART9-3',
      section: 'ART9',
      scfId: 'SCF-NET-005',
      ref: 'Art. 9(2)',
    }, // primary — cryptography (keys, transmission)
    { externalId: 'DORA-ART9-4', section: 'ART9', scfId: 'SCF-OPS-005', ref: 'Art. 9(4)(e)' }, // configuration management
    {
      primary: true,
      externalId: 'DORA-ART9-4',
      section: 'ART9',
      scfId: 'SCF-DEV-008',
      ref: 'Art. 9(4)(e)',
    }, // primary — change management (ICT changes)
    { externalId: 'DORA-ART9-5', section: 'ART9', scfId: 'SCF-NET-002', ref: 'Art. 9(4)(a)' }, // security of network services
    { externalId: 'DORA-ART9-5', section: 'ART9', scfId: 'SCF-NET-001', ref: 'Art. 9(4)(a)' }, // network security management
    {
      primary: true,
      externalId: 'DORA-ART9-5',
      section: 'ART9',
      scfId: 'SCF-NET-003',
      ref: 'Art. 9(4)(a)',
    }, // primary — network segregation

    // Article 10 — Detection
    { externalId: 'DORA-ART10-1', section: 'ART10', scfId: 'SCF-OPS-006', ref: 'Art. 10(1)' }, // security logging
    {
      primary: true,
      externalId: 'DORA-ART10-1',
      section: 'ART10',
      scfId: 'SCF-OPS-007',
      ref: 'Art. 10(1)',
    }, // primary — security monitoring (anomalous activity detection)
    { externalId: 'DORA-ART10-2', section: 'ART10', scfId: 'SCF-OPS-007', ref: 'Art. 10(2)' }, // security monitoring
    {
      primary: true,
      externalId: 'DORA-ART10-2',
      section: 'ART10',
      scfId: 'SCF-INC-002',
      ref: 'Art. 10(2)',
    }, // primary — incident assessment and classification (alert thresholds)

    // Article 11 — Response and recovery
    { externalId: 'DORA-ART11-1', section: 'ART11', scfId: 'SCF-BCM-001', ref: 'Art. 11(1)' }, // information security during disruption
    {
      primary: true,
      externalId: 'DORA-ART11-1',
      section: 'ART11',
      scfId: 'SCF-BCM-002',
      ref: 'Art. 11(1)',
    }, // primary — ICT continuity planning (ICT-BCP)
    { externalId: 'DORA-ART11-2', section: 'ART11', scfId: 'SCF-INC-003', ref: 'Art. 11(6)' }, // incident response and mitigation
    {
      primary: true,
      externalId: 'DORA-ART11-2',
      section: 'ART11',
      scfId: 'SCF-BCM-002',
      ref: 'Art. 11(6)',
    }, // primary — ICT continuity planning (annual testing)
    { externalId: 'DORA-ART11-3', section: 'ART11', scfId: 'SCF-GOV-002', ref: 'Art. 11(7)' }, // IS roles and responsibilities
    {
      primary: true,
      externalId: 'DORA-ART11-3',
      section: 'ART11',
      scfId: 'SCF-INC-001',
      ref: 'Art. 11(7)',
    }, // primary — incident management planning (crisis management function)

    // Article 12 — Backup policies and procedures
    { externalId: 'DORA-ART12-1', section: 'ART12', scfId: 'SCF-BCM-003', ref: 'Art. 12(1)' }, // primary — information backup (policy, scope, frequency, RTO/RPO)
    { externalId: 'DORA-ART12-2', section: 'ART12', scfId: 'SCF-OPS-011', ref: 'Art. 12(3)' }, // redundancy of processing facilities
    {
      primary: true,
      externalId: 'DORA-ART12-2',
      section: 'ART12',
      scfId: 'SCF-BCM-003',
      ref: 'Art. 12(3)',
    }, // primary — information backup (test, segregated storage)

    // Article 13 — Learning and evolving
    { externalId: 'DORA-ART13-1', section: 'ART13', scfId: 'SCF-INC-004', ref: 'Art. 13(2)' }, // primary — learning from incidents (post-incident review)
    { externalId: 'DORA-ART13-2', section: 'ART13', scfId: 'SCF-PPL-003', ref: 'Art. 13(6)' }, // primary — security awareness, education and training

    // Article 14 — Communication
    { externalId: 'DORA-ART14-1', section: 'ART14', scfId: 'SCF-GOV-005', ref: 'Art. 14' }, // contact with authorities
    {
      primary: true,
      externalId: 'DORA-ART14-1',
      section: 'ART14',
      scfId: 'SCF-INC-001',
      ref: 'Art. 14',
    }, // primary — incident management planning (crisis communication plan)

    // Article 16 — Simplified ICT risk-management framework
    { externalId: 'DORA-ART16-1', section: 'ART16', scfId: 'SCF-RSK-001', ref: 'Art. 16' }, // risk assessment process
    {
      primary: true,
      externalId: 'DORA-ART16-1',
      section: 'ART16',
      scfId: 'SCF-GOV-001',
      ref: 'Art. 16',
    }, // primary — IS policy (simplified framework baseline)

    // ── Chapter III — ICT-related incident management ────────────────────

    // Article 17 — ICT-related incident management process
    { externalId: 'DORA-ART17-1', section: 'ART17', scfId: 'SCF-INC-003', ref: 'Art. 17(1)' }, // incident response and mitigation
    {
      primary: true,
      externalId: 'DORA-ART17-1',
      section: 'ART17',
      scfId: 'SCF-INC-001',
      ref: 'Art. 17(1)',
    }, // primary — incident management planning
    { externalId: 'DORA-ART17-2', section: 'ART17', scfId: 'SCF-INC-002', ref: 'Art. 17(3)' }, // incident assessment and classification (early-warning indicators)
    {
      primary: true,
      externalId: 'DORA-ART17-2',
      section: 'ART17',
      scfId: 'SCF-PPL-008',
      ref: 'Art. 17(3)',
    }, // primary — security event reporting

    // Article 18 — Classification of incidents and cyber threats
    { externalId: 'DORA-ART18-1', section: 'ART18', scfId: 'SCF-INC-002', ref: 'Art. 18' }, // primary — incident assessment and classification

    // Article 19 — Reporting of major incidents and cyber threats
    { externalId: 'DORA-ART19-1', section: 'ART19', scfId: 'SCF-GOV-005', ref: 'Art. 19(1)' }, // contact with authorities
    {
      primary: true,
      externalId: 'DORA-ART19-1',
      section: 'ART19',
      scfId: 'SCF-INC-006',
      ref: 'Art. 19(1)',
    }, // primary — regulatory incident notification (initial/intermediate/final)
    { externalId: 'DORA-ART19-2', section: 'ART19', scfId: 'SCF-INC-007', ref: 'Art. 19(3)' }, // primary — incident communication to affected parties (client notification)
    { externalId: 'DORA-ART19-3', section: 'ART19', scfId: 'SCF-INC-006', ref: 'Art. 19(2)' }, // regulatory incident notification (voluntary threat notifications)
    {
      primary: true,
      externalId: 'DORA-ART19-3',
      section: 'ART19',
      scfId: 'SCF-GOV-007',
      ref: 'Art. 19(2)',
    }, // primary — threat intelligence (voluntary cyber-threat sharing)

    // ── Chapter IV — Digital operational resilience testing ──────────────

    // Article 24 — General requirements for testing
    { externalId: 'DORA-ART24-1', section: 'ART24', scfId: 'SCF-AUD-004', ref: 'Art. 24(1)' }, // vulnerability scanning programme
    { externalId: 'DORA-ART24-1', section: 'ART24', scfId: 'SCF-DEV-005', ref: 'Art. 24(1)' }, // security testing
    {
      primary: true,
      externalId: 'DORA-ART24-1',
      section: 'ART24',
      scfId: 'SCF-AUD-003',
      ref: 'Art. 24(1)',
    }, // primary — penetration testing programme (DORT programme anchor)
    { externalId: 'DORA-ART24-2', section: 'ART24', scfId: 'SCF-AUD-001', ref: 'Art. 24(5)' }, // primary — independent review of security (tester independence)

    // Article 25 — Testing of ICT tools and systems
    { externalId: 'DORA-ART25-1', section: 'ART25', scfId: 'SCF-AUD-004', ref: 'Art. 25(1)' }, // vulnerability scanning programme
    { externalId: 'DORA-ART25-1', section: 'ART25', scfId: 'SCF-DEV-005', ref: 'Art. 25(1)' }, // security testing
    {
      primary: true,
      externalId: 'DORA-ART25-1',
      section: 'ART25',
      scfId: 'SCF-AUD-003',
      ref: 'Art. 25(1)',
    }, // primary — penetration testing programme (yearly, critical systems)
    { externalId: 'DORA-ART25-2', section: 'ART25', scfId: 'SCF-OPS-004', ref: 'Art. 25(2)' }, // primary — vulnerability management (remediation)

    // Article 26 — Advanced testing (TLPT)
    { externalId: 'DORA-ART26-1', section: 'ART26', scfId: 'SCF-GOV-007', ref: 'Art. 26' }, // threat intelligence (threat-led aspect)
    {
      primary: true,
      externalId: 'DORA-ART26-1',
      section: 'ART26',
      scfId: 'SCF-AUD-003',
      ref: 'Art. 26',
    }, // primary — penetration testing programme (TLPT every 3 years)

    // Article 27 — Requirements for testers
    {
      primary: true,
      externalId: 'DORA-ART27-1',
      section: 'ART27',
      scfId: 'SCF-AUD-003',
      ref: 'Art. 27',
    }, // primary — penetration testing programme (qualified testers)

    // ── Chapter V — Managing of ICT third-party risk ─────────────────────

    // Article 28 — General principles
    { externalId: 'DORA-ART28-1', section: 'ART28', scfId: 'SCF-GOV-001', ref: 'Art. 28(2)' }, // IS policy (TPP strategy)
    { externalId: 'DORA-ART28-1', section: 'ART28', scfId: 'SCF-VDR-003', ref: 'Art. 28(2)' }, // ICT supply chain security
    {
      primary: true,
      externalId: 'DORA-ART28-1',
      section: 'ART28',
      scfId: 'SCF-VDR-001',
      ref: 'Art. 28(2)',
    }, // primary — supplier relationship management
    { externalId: 'DORA-ART28-2', section: 'ART28', scfId: 'SCF-AST-001', ref: 'Art. 28(3)' }, // asset inventory (dependencies)
    { externalId: 'DORA-ART28-2', section: 'ART28', scfId: 'SCF-VDR-003', ref: 'Art. 28(3)' }, // ICT supply chain security
    {
      primary: true,
      externalId: 'DORA-ART28-2',
      section: 'ART28',
      scfId: 'SCF-VDR-001',
      ref: 'Art. 28(3)',
    }, // primary — supplier relationship management (register of information)
    { externalId: 'DORA-ART28-3', section: 'ART28', scfId: 'SCF-VDR-003', ref: 'Art. 28(6)' }, // ICT supply chain security
    { externalId: 'DORA-ART28-3', section: 'ART28', scfId: 'SCF-RSK-001', ref: 'Art. 28(6)' }, // risk assessment process
    {
      primary: true,
      externalId: 'DORA-ART28-3',
      section: 'ART28',
      scfId: 'SCF-VDR-001',
      ref: 'Art. 28(6)',
    }, // primary — supplier relationship management (concentration risk)
    { externalId: 'DORA-ART28-4', section: 'ART28', scfId: 'SCF-BCM-002', ref: 'Art. 28(8)' }, // ICT continuity planning
    { externalId: 'DORA-ART28-4', section: 'ART28', scfId: 'SCF-VDR-005', ref: 'Art. 28(8)' }, // cloud service security (exit)
    {
      primary: true,
      externalId: 'DORA-ART28-4',
      section: 'ART28',
      scfId: 'SCF-VDR-001',
      ref: 'Art. 28(8)',
    }, // primary — supplier relationship management (exit strategies)

    // Article 29 — Preliminary assessment
    { externalId: 'DORA-ART29-1', section: 'ART29', scfId: 'SCF-RSK-001', ref: 'Art. 29' }, // risk assessment process
    { externalId: 'DORA-ART29-1', section: 'ART29', scfId: 'SCF-VDR-001', ref: 'Art. 29' }, // supplier relationship management
    {
      primary: true,
      externalId: 'DORA-ART29-1',
      section: 'ART29',
      scfId: 'SCF-VDR-002',
      ref: 'Art. 29',
    }, // primary — supplier agreements (pre-contract assessment)

    // Article 30 — Key contractual provisions
    { externalId: 'DORA-ART30-1', section: 'ART30', scfId: 'SCF-VDR-004', ref: 'Art. 30(2)' }, // supplier monitoring and review
    {
      primary: true,
      externalId: 'DORA-ART30-1',
      section: 'ART30',
      scfId: 'SCF-VDR-002',
      ref: 'Art. 30(2)',
    }, // primary — supplier agreements (mandatory provisions)
    { externalId: 'DORA-ART30-2', section: 'ART30', scfId: 'SCF-VDR-003', ref: 'Art. 30(3)' }, // ICT supply chain security
    {
      primary: true,
      externalId: 'DORA-ART30-2',
      section: 'ART30',
      scfId: 'SCF-VDR-002',
      ref: 'Art. 30(3)',
    }, // primary — supplier agreements (enhanced provisions for critical fns)

    // ── Chapter VI — Information-sharing arrangements ────────────────────

    // Article 45 — Information and intelligence sharing
    { externalId: 'DORA-ART45-1', section: 'ART45', scfId: 'SCF-GOV-006', ref: 'Art. 45' }, // contact with special interest groups
    {
      primary: true,
      externalId: 'DORA-ART45-1',
      section: 'ART45',
      scfId: 'SCF-GOV-007',
      ref: 'Art. 45',
    }, // primary — threat intelligence (trusted-community sharing)
  ],
};

module.exports = {
  FRAMEWORK_DORA,
};
